// The one WorldState the running game is playing.
//
// The game only ever has a single world, so it keeps a module-level pointer to
// it -- but the pointer lives here, in `game/`, rather than the *state* living
// scattered across five modules as it used to (see rune-lab/src/sim/state.ts for
// what moved and why). That distinction is the whole point of step 2: the sim
// owns the shape and knows nothing about there being a "current" one, so the
// world editor can create as many independent worlds as it likes without
// inheriting a global.
//
// Deliberately not a getter that lazily constructs: the world exists from module
// load, so importing this in any order always yields the same object, and the
// game's other modules can capture `getWorld()` results without worrying about
// initialisation sequence.
// Deliberately exposes no wholesale reset. The sim has `resetWorldState`, and the
// world editor's "Reset simulation" will want exactly that -- but the game's New
// Game is not the same operation and must not be quietly rewired to it. Clearing
// the learned-rune set, for instance, also has to write the cleared set back to
// localStorage (see learnedRunes.ts), which is persistence the sim knows nothing
// about; New Game additionally resets the notebook, tutorial flags, and dev
// settings, none of which are world state at all. ui.ts's New Game therefore keeps
// calling each module's own reset, exactly as before.
import { WorldState, createWorldState } from '../../rune-lab/src/sim/state';
import { clonePhysicsConfig } from '../../rune-lab/src/sim/physics';

// Its own mutable config rather than the shared frozen DEFAULT_PHYSICS
// (2026-08-12). The values are identical -- `clonePhysicsConfig` copies the
// defaults -- so nothing about how this world runs changed. What changed is that
// one of those values is now a player setting: `clock.msPerTurn` decides whether
// the world advances on the frame loop or on the player's actions, and
// settings.ts writes it here when the option is toggled (see
// applyClockSetting). Writing into the frozen defaults would have thrown, and
// thawing them would have retuned every other world in the process -- including
// the editor's, in a shared build.
const world: WorldState = createWorldState(clonePhysicsConfig());

export function getWorld(): WorldState {
  return world;
}

// A read-only handle for the Playwright suite (2026-08-12), and the reason it
// exists rather than the tests reading the HUD.
//
// Most of what the browser suite checks is visible by design -- a sprite moved, a
// panel opened, a bar shrank. Simulated time is not: the whole claim of turn mode
// is that `world.timeMs` stops advancing between actions, and there is no pixel
// anywhere on screen that says so. Asserting it through a derived readout (Mana
// regenerating, a reservoir refilling) would make the test about the readout's own
// thresholds and its 20-second interval instead, which is how a slow, flaky test
// gets written.
//
// Deliberately the live object rather than a copy: a copy would need refreshing,
// and a stale one is exactly the failure mode this is meant to detect. Nothing in
// the game ever reads this back -- it is a window onto the world, not a second
// route into it -- so removing it could only ever break tests.
(window as unknown as { __world: WorldState }).__world = world;
